import { useState } from 'react';
import type { StepProps, AnalysisResult } from '../OBWWizard';

// Phase 5c.5 (ADR-030 D2 Step 7) — persona adaptation step with funnel-stage
// chips rendered as cards (the demo's persona-chip pattern: Found / Engaged /
// Nearly / Converted). Pre-filled from AI website analysis when present.
//
// The OBW data model captures heroHeadline + audienceNoun + placeNoun + per-
// funnel-stage labels and descriptions. This step exposes those fields with
// the demo's card-based layout, not a single dropdown.

interface FunnelStage { icon: string; label: string; description: string; chipKind?: 'found' | 'engaged' | 'nearly' | 'converted'; }

const DEFAULT_FUNNEL_TEMPLATE: FunnelStage[] = [
  { icon: '🔍', label: 'Found', description: 'Discovered your business via search or referral.', chipKind: 'found' },
  { icon: '👀', label: 'Engaged', description: 'Visited multiple pages or returned for a second look.', chipKind: 'engaged' },
  { icon: '🎯', label: 'Nearly', description: 'Reached a high-intent page (pricing, demo, contact).', chipKind: 'nearly' },
  { icon: '✅', label: 'Converted', description: 'Completed the primary conversion action.', chipKind: 'converted' },
];

export function StepPersona({ data, analysisResult, onNext, isRequired }: StepProps & { analysisResult?: AnalysisResult | null }) {
  const ai = analysisResult;

  const [heroHeadline, setHeroHeadline] = useState<string>(
    (data?.heroHeadline as string) ?? ai?.heroHeadline ?? '',
  );
  const [audienceNoun, setAudienceNoun] = useState<string>(
    (data?.audienceNoun as string) ?? ai?.audienceNoun ?? '',
  );
  const [placeNoun, setPlaceNoun] = useState<string>(
    (data?.placeNoun as string) ?? ai?.placeNoun ?? '',
  );
  const initialFunnel = (data?.funnel as FunnelStage[]) ?? ai?.funnel ?? DEFAULT_FUNNEL_TEMPLATE;
  const [funnel, setFunnel] = useState<FunnelStage[]>(initialFunnel);

  function updateFunnelStage(idx: number, field: keyof FunnelStage, value: string) {
    setFunnel((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  }

  function commit() {
    onNext({ heroHeadline, audienceNoun, placeNoun, funnel });
  }

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">Persona adaptation</h2>
      <p className="obw-subtitle">
        Nexanomics personalises trust signals using your audience model.
        {ai ? " We've pre-filled these from your website — review and adjust." : ' Fill in the fields below to tailor your dashboard.'}
      </p>

      {ai && (
        <div className="obw-ai-status">
          <span className="obw-ai-done" aria-hidden="true">✓</span>
          <span>Pre-filled from <strong style={{ color: 'var(--text)' }}>{ai.domainUrl}</strong> analysis</span>
        </div>
      )}

      <p className="obw-form-section">Audience</p>
      <div className="obw-form-grid-2">
        <div className="obw-form-group">
          <label className="obw-form-label" htmlFor="obw-audience-noun">Who you serve</label>
          <input
            id="obw-audience-noun"
            className="obw-form-input"
            type="text"
            value={audienceNoun}
            onChange={(e) => setAudienceNoun(e.target.value)}
            placeholder="e.g. customers, clients, subscribers"
          />
        </div>
        <div className="obw-form-group">
          <label className="obw-form-label" htmlFor="obw-place-noun">Where they reach you</label>
          <input
            id="obw-place-noun"
            className="obw-form-input"
            type="text"
            value={placeNoun}
            onChange={(e) => setPlaceNoun(e.target.value)}
            placeholder="e.g. inbox, dashboard, storefront"
          />
        </div>
      </div>

      <div className="obw-form-group">
        <label className="obw-form-label" htmlFor="obw-hero-headline">Hero headline (how you describe your value)</label>
        <input
          id="obw-hero-headline"
          className="obw-form-input"
          type="text"
          value={heroHeadline}
          onChange={(e) => setHeroHeadline(e.target.value)}
          placeholder="e.g. Build trust with every email"
        />
      </div>

      <p className="obw-form-section">Funnel stages</p>
      <p className="obw-form-hint" style={{ marginTop: 0, marginBottom: '12px' }}>
        Label each stage of your visitor journey. Reports use these labels to describe where
        visitors are.
      </p>
      <div className="obw-persona-grid">
        {funnel.map((stage, i) => (
          <div key={i} className="obw-persona-card" aria-label={`Funnel stage ${i + 1}: ${stage.label}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '20px' }} aria-hidden="true">{stage.icon}</span>
              {stage.chipKind && (
                <span className={`obw-persona-chip ${stage.chipKind}`}>{stage.label}</span>
              )}
            </div>
            <input
              className="obw-form-input"
              type="text"
              value={stage.label}
              onChange={(e) => updateFunnelStage(i, 'label', e.target.value)}
              placeholder="Stage label"
              aria-label={`Stage ${i + 1} label`}
            />
            <input
              className="obw-form-input"
              type="text"
              value={stage.description}
              onChange={(e) => updateFunnelStage(i, 'description', e.target.value)}
              placeholder="Stage description"
              aria-label={`Stage ${i + 1} description`}
            />
          </div>
        ))}
      </div>

      <div className="portal-wizard-nav">
        {!isRequired && (
          <button type="button" className="btn-secondary" onClick={commit}>Skip for now</button>
        )}
        <button type="button" className="obw-btn-primary" onClick={commit}>Continue</button>
      </div>
    </div>
  );
}
